import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { UploadFileButton } from "@/components/Button/UploadFileButton";
import { DataTable } from "@/components/DataTable/DataTable";
import { type Column, type Data, parseData } from "@/components/DataTable/parser";
import { downloadExcel, parseExcel } from "@/components/DataTable/sheet";
import { validate } from "@/components/DataTable/validation";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { HOUSE_NUMBER_WITH_SUFFIX_REGEX, splitCombinedAddressColumn, splitHouseNumber } from "@/helpers/address";
import { parseFileContent } from "@/helpers/file-parse";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnDropFiles } from "@/hooks/useOnDropFiles";
import { useOnPaste } from "@/hooks/useOnPaste";
import { usePermission } from "@/hooks/usePermission";
import { usePrompt } from "@/hooks/usePrompt";
import { useSlug } from "@/hooks/useSlug";
import { addressMutations } from "@/queries/addresses/mutations";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function AddressBulkUploadPage(): React.ReactNode {
  const [data, setData] = useState<Data<NonNullable<typeof columns>>>();
  const [isUploading, setIsUploading] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File>();

  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const canManageRems = hasPermission((x) => x.integrations.canManageRems);
  const showFlashToast = useFlashToast();
  const buildingQueries = useBuildingQueries();
  const integrationQueries = useIntegrationQueries();

  const bulkUploadAddressMutation = addressMutations.useBulkUploadAddress();
  const { mutateAsync: uploadDocumentAsync } = fileMutations.useAddDocument();
  const {
    data: buildings,
    isPending: isPendingBuildings,
    error: buildingsError,
  } = useQuery({
    ...buildingQueries.list(),
  });
  const {
    data: remsConfiguration,
    isPending: isPendingRemsConfiguration,
    error: remsConfigurationError,
  } = useQuery({
    ...integrationQueries.remsConfiguration(),
    enabled: canManageRems,
  });
  const canUploadRemsMappings = canManageRems && remsConfiguration?.contracts.enabled === true;

  usePrompt(t("common.label.wait-and-not-exit-page"), isUploading);

  const columns = useMemo(() => {
    if (!isDefined(buildings)) {
      return undefined;
    }

    return [
      {
        name: "Building",
        alias: ["gebouw", "gebouw naam", "building name"],
        rules: {
          required: t("page.address-bulk-upload.validation.building.required"),
          oneOf: {
            error: t("page.address-bulk-upload.validation.building.not-one-of"),
            values: () => buildings.items.map((x) => x.name),
          },
        },
      },
      {
        name: "Street",
        alias: ["straatnaam", "straat", "street name", "streetname", "adres", "address"],
        rules: { required: t("page.address-bulk-upload.validation.street-name.required") },
      },
      {
        name: "House number",
        alias: ["huisnummer", "housenumber", "huisnr", "huisnr."],
        rules: {
          required: t("page.address-bulk-upload.validation.house-number.required"),
          match: {
            error: t("page.address-bulk-upload.validation.house-number.is-not-number"),
            regex: HOUSE_NUMBER_WITH_SUFFIX_REGEX,
          },
        },
      },
      {
        name: "House number suffix",
        alias: ["house number suffix", "huisnummertoevoeging"],
      },
      {
        name: "Postal code",
        alias: ["postcode", "zipcode", "zip"],
        rules: { required: t("page.address-bulk-upload.validation.postal-code.required") },
      },
      {
        name: "City",
        alias: ["stad", "stad naam", "city name", "plaats", "plaatsnaam"],
        rules: { required: t("page.address-bulk-upload.validation.city.required") },
      },
      { name: "Type", alias: ["woning type", "address type", "eigenaar"] },
      { name: "Floor", alias: ["verdieping", "floor", "level"] },
      ...(canUploadRemsMappings
        ? [
            {
              name: "REMS address ID",
              alias: ["rems vhe id", "vhe id", "rems id"],
            },
          ]
        : []),
    ] satisfies Column[];
  }, [t, buildings, canUploadRemsMappings]);

  async function upload() {
    if (!data) {
      throw new Error("Data missing");
    }

    if (!dataState?.valid) {
      throw new Error("Data is invalid");
    }

    try {
      setIsUploading(true);
      setData((oldData) =>
        oldData?.map((row) =>
          row.uploadStatus === "SUCCESS" ? row : ({ ...row, uploadStatus: "QUEUED" } as typeof row),
        ),
      );

      // Upload the file to the backend first (for logging/audit purposes)
      if (uploadedFile) {
        await uploadDocumentAsync({ file: uploadedFile });
      }

      let successCount = 0;
      for (let idx = 0; idx < data.length; idx++) {
        const row = data[idx];
        if (row.uploadStatus === "SUCCESS") {
          successCount++;
          continue;
        }

        updateValue(idx, "uploadStatus", "UPLOADING");
        const rowData = row as { [k in NonNullable<typeof columns>[number]["name"]]: string };
        try {
          // The house number column may carry a glued-on suffix (e.g. "12a"), so split it into its
          // number + suffix. An explicit "House number suffix" column takes precedence.
          const parsedHouseNumber = splitHouseNumber(rowData["House number"] ?? "");
          if (!parsedHouseNumber) {
            throw new Error("Invalid house number");
          }

          await bulkUploadAddressMutation.mutateAsync({
            buildingId: mapBuildingValue(rowData.Building)!,
            city: rowData.City,
            floor: rowData.Floor || undefined,
            housenumber: parsedHouseNumber.houseNumber,
            housenumberSuffix: rowData["House number suffix"] || parsedHouseNumber.houseNumberSuffix,
            postalCode: rowData["Postal code"],
            streetName: rowData.Street,
            type: rowData.Type || undefined,
            remsAddressId: canUploadRemsMappings ? rowData["REMS address ID"]?.trim() || undefined : undefined,
          });
          updateValue(idx, "uploadStatus", "SUCCESS");
          successCount++;
        } catch {
          updateValue(idx, "uploadStatus", "ERROR");
        }
      }

      showFlashToast({
        message: t("page.address-bulk-upload.notification.success", { count: successCount }),
        variant: "success",
      });
    } finally {
      setIsUploading(false);
      setData((oldData) =>
        oldData?.map((row) => (row.uploadStatus === "QUEUED" ? ({ ...row, uploadStatus: "NONE" } as typeof row) : row)),
      );
    }
  }

  function mapBuildingValue(buildingName: string) {
    if (!buildings) {
      throw new Error("No buildings available yet");
    }

    for (const building of buildings.items) {
      if (buildingName.toLowerCase() === building.name.toLowerCase()) {
        return building.id;
      }
    }
  }

  function updateValue(row: number, column: string, value: string) {
    setData((oldData) => {
      if (!oldData) {
        throw new Error("No existing data");
      }

      const newData = [...oldData];
      const rowNumber = Number(row);
      const oldRow = oldData[rowNumber];
      const newRow = { ...oldRow, [column]: value };
      newData.splice(rowNumber, 1, newRow);

      return newData;
    });
  }

  function removeRow(row: number) {
    setData((oldData) => {
      if (!oldData) {
        return;
      }

      const newData = [...oldData];
      newData.splice(row, 1);

      // No more data remaining, reset form
      if (newData.length === 0) {
        return undefined;
      }

      return newData;
    });
  }

  const loadCsv = useCallback(
    async (...args: Parameters<typeof parseExcel>) => {
      try {
        setIsParsingFile(true);
        const rows = await parseExcel(...args);
        setData(parseData(splitCombinedAddressColumn(rows as object[]), columns!));
      } finally {
        setIsParsingFile(false);
      }
    },
    [columns],
  );

  useOnDropFiles(
    useCallback(
      async (files) => {
        const file = files?.[0].getAsFile();
        if (file) {
          try {
            const content = await parseFileContent(file);
            await loadCsv(content);
          } catch (error) {
            alert(error);
          }
        }
      },
      [loadCsv],
    ),
    !data && !isParsingFile,
  );

  useOnPaste(
    useCallback(
      (rawData) => {
        void loadCsv(rawData).catch((error) => alert(`Could not paste data: ${error}`));
      },
      [loadCsv],
    ),
    !data && !isParsingFile,
  );

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const file = target.files?.[0];

    if (file) {
      try {
        setUploadedFile(file);
        const contents = await parseFileContent(file);
        await loadCsv(contents);
      } catch (error) {
        alert(error);
        target.value = "";
      }
    }
  };

  // oxlint-disable-next-line react-hooks/preserve-manual-memoization
  const dataState = useMemo(() => (data && columns ? validate(columns, data) : undefined), [data, columns]);
  const isBulkUploadSuccessful = data?.every((row) => row.uploadStatus === "SUCCESS");

  if (isPendingBuildings || (canManageRems && isPendingRemsConfiguration)) {
    return <FullSizeLoader withPadding />;
  }

  if (!columns || buildingsError || remsConfigurationError) {
    return (
      <ErrorPage error={buildingsError || remsConfigurationError || t("page.address-bulk-upload.error.no-columns")} />
    );
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.address-bulk-upload.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.addresses.title"),
              to: routes.addresses.list({
                slug,
              }),
            },
            {
              name: t("page.address-bulk-upload.title"),
            },
          ]}
        />
      }
      actions={
        <>
          {data && isBulkUploadSuccessful && (
            <Button
              onClick={() => {
                setData(undefined);
              }}
            >
              {t("page.company-bulk-upload.reset")}
            </Button>
          )}
          {!data && (
            <UploadFileButton
              disabled={isUploading}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={onUploadFile}
            >
              {t("page.address-bulk-upload.button")}
            </UploadFileButton>
          )}
        </>
      }
    >
      <SectionCard>
        {data && (
          <Stack gap="xl" align="end">
            <DataTable
              data={data}
              columns={columns}
              dataState={dataState!}
              onRemoveRow={removeRow}
              onUpdateValue={updateValue}
              disabled={isUploading}
              data-testid="address-data-table"
            />
            {!isBulkUploadSuccessful && (
              <Button data-testid="submit-btn" onClick={upload} isLoading={isUploading} disabled={!dataState?.valid}>
                {t("page.address-bulk-upload.submit")}
              </Button>
            )}
          </Stack>
        )}
        {!data && isParsingFile && <FullSizeLoader withPadding />}
        {!data && !isParsingFile && (
          <>
            <p className="max-w-prose whitespace-pre-wrap">{t("page.address-bulk-upload.prose")}</p>
            <Button
              variant="ghostPrimary"
              icon={<Download01Icon />}
              onClick={() =>
                downloadExcel(`address-upload-${sessionUser.project.name}`, [
                  columns.map((x) => x.name),
                  ...buildings.items.map((x) => [x.name]),
                ])
              }
            >
              {t("page.address-bulk-upload.example-file")}
            </Button>
          </>
        )}
      </SectionCard>
    </DocumentPage>
  );
}
